import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import { useSlidePackages } from './hooks/useSlidePackages';
import { SlidePackagesTable, SlidePackageDialog } from './components';
import { SlidePackagesPageProps } from './types';

export default function SlidePackagesPage({ isDark }: SlidePackagesPageProps) {
  const {
    packages,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  } = useSlidePackages();

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('uz-UZ', {
      style: 'currency',
      currency: 'UZS',
      minimumFractionDigits: 0,
    }).format(price);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            Slide Packages
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            One-shot deck packs for the Telegram slides bot
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Package
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark
            ? 'bg-[#1E293B] shadow-lg shadow-black/10'
            : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <SlidePackagesTable
          isDark={isDark}
          packages={packages}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          formatPrice={formatPrice}
        />
      </Card>

      <SlidePackageDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        title="Create Slide Package"
        description="Add a new deck pack for the Telegram bot"
        submitLabel="Create"
      />

      <SlidePackageDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        title="Edit Slide Package"
        description="Update package information"
        submitLabel="Save Changes"
      />
    </div>
  );
}
